import React, { memo, useMemo, useState } from 'react';
import { FieldArray, Formik, FormikProps } from 'formik';
import { Form } from 'formik-antd';
import useDeepCompareEffect from 'use-deep-compare-effect';

import {
  PayTypeEnum,
  OvertimeEnum,
  AssignmentTypeEnum,
  CustomerMspOrVmsOverviewDto,
} from 'api/backofficeApiClient';
import FormikEffect from 'components/formik/FormikEffect';
import {
  isAdminSelector,
  isTeammateSelector,
} from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import FinancialInfoScheduleModal, {
  EffectiveDateMode,
  FinancialSchedulerFormValues,
} from 'components/modals/FinancialInfoScheduleModal';
import { IS_PRODUCTION_ENV, IS_QA_ENV } from 'constants/env.constants';
import FinancialInfoDetails from './FinancialInfoDetails';
import FinancialRatesSectionArray from './FinancialRatesSectionArray';
import { getSubmitValidationSchema } from './validation';
import { MULTI_RATES_KEY } from 'hooks/useFinancialInfoTabs';
import FinancialSchedulerContext from 'components/FinancialSchedulerContext';
import { getPTOMultiRateDetails } from 'utils/multiRates/multiRatesUtils';
import { payCodesDetailsInfoSelector } from 'redux/selectors/assignmentSelectors';
import { mapMultiRates } from '../mapUtils';
import { AssignmentInfoFormValues } from '../AssignmentInfo';

import styles from './FinancialInfo.module.less';

export interface WorkSiteWorkLocation {
  country?: number;
  state?: number;
  internationalStateName?: string;
  city?: string;
  internationalCityName?: string;
  address?: string;
  zip?: string;
}

export interface SimplePriceAgreementW2Info {
  w2Price?: number;
  placementStates?: (number | undefined)[];
}

export type FinancialInfoProps = {
  formRef: any;
  isPayTypeVisible?: boolean;
  initialValues?: FinancialInfoFormValues;
  mspOrVmsOverviewInfo?: CustomerMspOrVmsOverviewDto;
  workSiteWorkLocation: WorkSiteWorkLocation | null;
  selectedAssignmentType: AssignmentTypeEnum | null;
  onChange: (formik: FormikProps<FinancialInfoFormValues>) => void;
  w2InfoByWorkSiteWorkLocation?: SimplePriceAgreementW2Info;
  initialMBPRate?: number;
  predefinedFinancialSchedulerValues?: FinancialSchedulerFormValues;
  onSetPredefinedFinancialScheduler: (
    data: FinancialSchedulerFormValues,
  ) => void;
  onResetFinancialInfoSchedulers: () => void;
  assignmentInfoForm?: FormikProps<AssignmentInfoFormValues>;
};

export interface PayCodeInfoFormValues {
  id?: string;
  payCodeId?: string;
  billRate?: number;
  payRate?: number;
  specialInstructions?: string;
  memberBilledIndicator?: boolean;
  hasPayRateCustomLogic?: boolean;
  hasBillRateCustomLogic?: boolean;
}

export interface MultiRateFormValues {
  id?: string;
  payTypeId?: string;
  activePayCodeId?: string;
  payCodesInfo?: PayCodeInfoFormValues[];
}

export interface FinancialInfoFormValues {
  vmsRate?: number | null;
  mspRate?: number | null;
  overrideVmsRate?: boolean;
  overrideMspRate?: boolean;
  vmpPortion?: number | null;
  mspPortion?: number | null;
  mbpRate?: number | null;
  billRate?: number | null;
  payRate?: number | null;
  profitRate?: number | null;
  overtimePayRate?: number | null;
  overtimeBillRate?: number | null;
  overtime?: OvertimeEnum | null;
  payType?: PayTypeEnum | null;
  dtBillRate?: number | null;
  dtPayRate?: number | null;
  multiRates?: MultiRateFormValues[];
}

export default memo((props: FinancialInfoProps) => {
  const {
    formRef,
    onChange,
    mspOrVmsOverviewInfo,
    workSiteWorkLocation,
    selectedAssignmentType,
    initialValues: predefinedInitialValues,
    w2InfoByWorkSiteWorkLocation,
    initialMBPRate,
    onSetPredefinedFinancialScheduler,
    onResetFinancialInfoSchedulers,
    predefinedFinancialSchedulerValues,
    assignmentInfoForm,
  } = props;
  const [
    isFinancialInfoSchedulerModalVisible,
    setIsFinancialInfoSchedulerModalVisible,
  ] = useState(false);
  const isAdmin = useAppSelector(isAdminSelector);
  const isW2Assignment =
    selectedAssignmentType === AssignmentTypeEnum.EEAssignment;
  const isTeammate = useAppSelector(isTeammateSelector);
  const payCodesDetails = useAppSelector(payCodesDetailsInfoSelector);
  // State Ids are different on prod and dev.
  const californiaStateId = IS_PRODUCTION_ENV || IS_QA_ENV ? 5 : 210;
  const [overtimeValue, setOvertimeValue] = useState(
    predefinedInitialValues?.overtime,
  );
  const isViewDTBillRateDtPayRateFields =
    overtimeValue === OvertimeEnum.Nonexempt &&
    workSiteWorkLocation?.state === californiaStateId;

  const mapFinancialInfoToInitialValues = (): FinancialInfoFormValues => {
    if (predefinedInitialValues)
      return { ...predefinedInitialValues, mbpRate: initialMBPRate };

    return {
      vmsRate: 0,
      mspRate: 0,
      overrideVmsRate: false,
      overrideMspRate: false,
      billRate: null,
      payRate: null,
      overtimeBillRate: null,
      overtimePayRate: null,
      overtime: null,
      payType: null,
      profitRate: null,
      mbpRate: initialMBPRate,
      multiRates: [],
      dtBillRate: null,
    };
  };

  const initialValues = mapFinancialInfoToInitialValues();

  useDeepCompareEffect(() => {
    if (!formRef.current) return;
    const { setFieldValue } = formRef.current;

    setFieldValue('billRate', initialValues.billRate);
    setFieldValue('payRate', initialValues.payRate);
    setFieldValue('overtimeBillRate', initialValues.overtimeBillRate);
    setFieldValue('overtimePayRate', initialValues.overtimePayRate);
    setFieldValue('overtime', initialValues.overtime);
    setFieldValue('payType', initialValues.payType);
    setFieldValue('profitRate', initialValues.profitRate);
    setFieldValue('mbpRate', initialValues.mbpRate);
    setFieldValue('dtBillRate', initialValues.dtBillRate);
    setFieldValue('dtPayRate', initialValues.dtPayRate);
    setFieldValue('multiRates', initialValues.multiRates);
  }, [initialValues]);

  const handleOpenFinancialInfoSchedulerModal = () => {
    setIsFinancialInfoSchedulerModalVisible(true);
  };

  const handleCloseFinancialInfoSchedulerModal = () => {
    setIsFinancialInfoSchedulerModalVisible(false);
  };

  const submitValidationSchema = useMemo(
    () =>
      getSubmitValidationSchema(
        isW2Assignment,
        isViewDTBillRateDtPayRateFields,
        predefinedFinancialSchedulerValues,
        isTeammate,
        mspOrVmsOverviewInfo,
      ),
    [
      isW2Assignment,
      isTeammate,
      isViewDTBillRateDtPayRateFields,
      predefinedFinancialSchedulerValues,
      mspOrVmsOverviewInfo,
    ],
  );

  const onRemoveFinancialScheduler = () => {
    onResetFinancialInfoSchedulers();
    handleCloseFinancialInfoSchedulerModal();
  };

  return (
    <FinancialSchedulerContext.Provider
      value={{
        predefinedFinancialSchedulerValues: predefinedFinancialSchedulerValues,
        workSiteWorkLocation: workSiteWorkLocation,
        w2InfoByWorkSiteWorkLocation: w2InfoByWorkSiteWorkLocation,
        isW2Assignment: isW2Assignment,
        isAdmin: isAdmin,
        isViewDTBillRateDtPayRateFields: isViewDTBillRateDtPayRateFields,
        jobCategory: assignmentInfoForm?.values.jobCategory,
        mspOrVmsOverviewInfo,
      }}
    >
      {isFinancialInfoSchedulerModalVisible && (
        <FinancialInfoScheduleModal
          mode={EffectiveDateMode.EDIT}
          initialValues={predefinedFinancialSchedulerValues}
          onClose={handleCloseFinancialInfoSchedulerModal}
          onSuccess={onSetPredefinedFinancialScheduler}
          onRemoveFinancialScheduler={onRemoveFinancialScheduler}
          mspOrVmsOverviewInfo={mspOrVmsOverviewInfo}
          workStateLocation={workSiteWorkLocation?.state}
          isW2Assignment={isW2Assignment}
          assignmentType={selectedAssignmentType}
          w2InfoByWorkSiteWorkLocation={w2InfoByWorkSiteWorkLocation}
          ptoMultiRate={getPTOMultiRateDetails(
            payCodesDetails,
            mapMultiRates(formRef?.current?.values?.multiRates),
          )}
          jobCategoryValue={assignmentInfoForm?.values?.jobCategory}
        />
      )}

      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={submitValidationSchema}
        onSubmit={() => {}}
      >
        {() => (
          <>
            <FormikEffect onChange={onChange} />

            <Form layout="vertical">
              <div className="modal-scroll-wrapper">
                {!isAdmin && (
                  <div className={styles.hint}>
                    <i className="icon icon-info-filled" />
                    All calculations are based upon billing and pay rates for
                    Regular Pay
                  </div>
                )}

                <FieldArray
                  name={MULTI_RATES_KEY}
                  render={(props) => (
                    <FinancialRatesSectionArray
                      {...props}
                      renderFinancialInfoSection={
                        <FinancialInfoDetails
                          formRef={formRef}
                          handleOpenFinancialInfoSchedulerModal={
                            handleOpenFinancialInfoSchedulerModal
                          }
                          isTeammate={isTeammate}
                          setOvertimeValue={setOvertimeValue}
                        />
                      }
                      selectedAssignmentType={selectedAssignmentType}
                      jobCategory={assignmentInfoForm?.values?.jobCategory}
                    />
                  )}
                />
              </div>
            </Form>
          </>
        )}
      </Formik>
    </FinancialSchedulerContext.Provider>
  );
});
